import { API_URL, DEFAULT_LANGUAGE, ROUTES } from '../constants';
import { SessionState, Language } from '../types';
import { locales } from '../locales';

/**
 * CENTRALIZED SESSION MANAGER
 */

const JOISST_AUTH_URL = 'https://api.joisst.com';
const JOISST_API_URL = 'https://api.joisst.com';

const resolveLanguage = (apiLang?: string): Language => {
  if (apiLang && Object.values(Language).includes(apiLang as Language)) {
    return apiLang as Language;
  }
  const browserLang = navigator.language.split('-')[0] as Language;
  if (Object.values(Language).includes(browserLang)) {
    return browserLang;
  }
  return DEFAULT_LANGUAGE;
};

export const SessionManager = {
  /**
   * Captures referral codes from URL and stores in SessionStorage
   */
  captureReferral: () => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const ref = urlParams.get('ref') || urlParams.get('referral');
      if (ref) {
        sessionStorage.setItem('joisst_referral', ref);
      }
    } catch (e) {
      console.warn('Referral capture failed', e);
    }
  },

  /**
   * Loads the full session context.
   * Hands off 'ref' to the BFF to ensure platform attribution.
   */
  loadSession: async (): Promise<SessionState> => {
    try {
      const storedRef = sessionStorage.getItem('joisst_referral');
      const fetchUrl = storedRef 
        ? `/api/session?ref=${encodeURIComponent(storedRef)}`
        : '/api/session';

      const response = await fetch(fetchUrl, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include' 
      });

      if (!response.ok) {
        throw new Error(`Failed to load session: ${response.status}`);
      }

      const data = await response.json();

      if (!data.isAuthenticated || !data.user) {
        return {
          user: {
            id: 'anon',
            maskedEmail: 'guest@joisst.com',
            isAdmin: false,
            language: resolveLanguage(),
            cookieConsent: false,
            createdAt: new Date().toISOString(),
            lastLoginAt: new Date().toISOString(),
          },
          apiData: {
            userId: 'anon',
            email: 'guest@joisst.com',
            hearts: 1,
            brains: 0,
            isAdmin: false,
            language: resolveLanguage(),
            currency: 'USD',
            referralCode: '',
            theme: 'light',
            cookieConsent: false,
          },
          isLoading: false,
          isAuthenticated: false,
          language: resolveLanguage(),
        };
      }

      const finalLanguage = resolveLanguage(data.user.language);

      return {
        user: data.user,
        apiData: data.apiData,
        isLoading: false,
        isAuthenticated: true,
        language: finalLanguage,
      };

    } catch (error) {
      console.error('Session Manager Error:', error);
      return {
        user: null,
        apiData: null,
        isLoading: false,
        isAuthenticated: false,
        language: resolveLanguage(),
        debugMessage: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  },

  redirectToLogin: () => {
    const returnTo = encodeURIComponent(window.location.href);
    let authUrl = `${JOISST_AUTH_URL}?returnTo=${returnTo}`;
    const storedRef = sessionStorage.getItem('joisst_referral');
    if (storedRef) {
      authUrl += `&ref=${encodeURIComponent(storedRef)}`;
    }
    window.location.href = authUrl;
  },

  logout: async () => {
    try {
      await fetch(`${JOISST_API_URL}/api/auth?action=logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (e) {
      console.error("Logout failed", e);
    } finally {
      window.location.href = '/';
    }
  },

  updateCookieConsent: async (consent: boolean): Promise<boolean> => {
    try {
      const res = await fetch(`${JOISST_API_URL}/api/user?action=cookie_consent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ consent })
      });
      return res.ok;
    } catch (e) {
      console.error("Failed to update cookie consent", e);
      return false;
    }
  },

  redirectToHearts: () => {
    const returnTo = encodeURIComponent(window.location.href);
    let url = `${JOISST_API_URL}/hearts?returnTo=${returnTo}`;
    const storedRef = sessionStorage.getItem('joisst_referral');
    if (storedRef) url += `&ref=${encodeURIComponent(storedRef)}`;
    window.location.href = url;
  },

  redirectToBrains: () => {
    const returnTo = encodeURIComponent(window.location.href);
    let url = `${JOISST_API_URL}/brains?returnTo=${returnTo}`;
    const storedRef = sessionStorage.getItem('joisst_referral');
    if (storedRef) url += `&ref=${encodeURIComponent(storedRef)}`;
    window.location.href = url;
  },

  spendHearts: async (amount: number = 1): Promise<number | null> => {
    try {
      const response = await fetch(`${JOISST_API_URL}/api/hearts?action=spend`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ amount }),
      });
      if (response.status === 402) {
        SessionManager.redirectToHearts();
        return null;
      }
      if (!response.ok) return null;
      const data = await response.json();
      return data.hearts;
    } catch (error) {
      return null;
    }
  },

  spendBrains: async (amount: number = 1): Promise<number | null> => {
    try {
      const response = await fetch(`${JOISST_API_URL}/api/brains?action=spend`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ amount }),
      });
      if (response.status === 402) {
        SessionManager.redirectToBrains();
        return null;
      }
      if (!response.ok) return null;
      const data = await response.json();
      return data.brains;
    } catch (error) {
      return null;
    }
  }
};

export const t = (key: keyof typeof locales['en'], lang: Language): string => {
  const dict = locales[lang] || locales[DEFAULT_LANGUAGE];
  return dict[key] || String(key);
};